import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import { getAriaAttributes } from '../../util/getAriaAttributes/getAriaAttributes.js';
import { selectToken } from '../../util/tokenSelector.js';
import useForwardedRef from '../../util/useForwardedRef.js';
import { Box } from '../Box/Box.js';
import { IconWrapper } from '../FormField/Common/IconWrapper.js';
import { InputBase as InputBaseMemoized } from '../FormField/InputBase/InputBase.js';
import { useGroupContext } from '../Group/GroupContext.js';
import { Typography } from '../Typography/Typography.js';
import { useExperimentContext } from '../../contexts/session/Experiment.js';
import { useThemeContext } from '../../contexts/session/Theme.js';
import styled from 'styled-components';
import { searchFieldStylePropsMapper } from './SearchField.mapper.js';
import { interactiveStyle } from './searchFieldInteractiveState.js';

var _excluded = ["variant", "leadingAsset", "trailingAsset", "placeholderText", "inputText", "labelText", "label", "clearLabel", "state", "width", "onClick", "onChange", "onFocus", "onBlur", "onClear", "id", "required", "selectOnFocus", "role"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var iconSize = '20';
var SearchFieldContainer = /*#__PURE__*/styled(Box).withConfig({
  displayName: "SearchField__SearchFieldContainer",
  componentId: "sc-1s2srj9-0"
})(function (props) {
  var radius = props.$radius ? "clamp(0px, ".concat(props.$radius, ", 22px)") : '22px';
  return _objectSpread({
    // Need to use exact height/2 of the component to set border radius
    borderTopLeftRadius: radius,
    borderTopRightRadius: radius,
    borderBottomLeftRadius: radius,
    borderBottomRightRadius: radius
  }, props.$interactiveState);
});
var LeadingIconElement = /*#__PURE__*/React.memo(function (props) {
  return props.leadingAsset ? /*#__PURE__*/React.createElement(IconWrapper, {
    id: props.leadingAsset.id,
    size: iconSize,
    me: "12",
    label: props.leadingAsset.label,
    color: props.inputTextColor
  }) : null;
});
var TrailingIconElement = /*#__PURE__*/React.memo(function (props) {
  if (!props.trailingAsset) return null;
  return /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "auto",
    disabled: props.disabled,
    cursor: props.disabled ? 'auto' : 'pointer',
    as: "button",
    "aria-label": props.trailingAsset.label,
    p: "4",
    tabIndex: props.disabled ? -1 : 0
  }, /*#__PURE__*/React.createElement(IconWrapper, {
    id: props.trailingAsset.id,
    size: iconSize,
    color: props.inputTextColor
  }));
});

/**
 * The search field component is like a reliable GPS for your app, helping users navigate to their desired destination with ease. The search field picker allows users to select a value from a list of predefined options.
 */
var SearchField = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var variant = _ref.variant,
    leadingAsset = _ref.leadingAsset,
    trailingAsset = _ref.trailingAsset,
    placeholderText = _ref.placeholderText,
    inputText = _ref.inputText,
    labelText = _ref.labelText,
    label = _ref.label,
    clearLabel = _ref.clearLabel,
    state = _ref.state,
    _ref$width = _ref.width,
    width = _ref$width === void 0 ? 'parent-fit' : _ref$width,
    onClick = _ref.onClick,
    onChange = _ref.onChange,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    onClear = _ref.onClear,
    id = _ref.id,
    required = _ref.required,
    selectOnFocus = _ref.selectOnFocus,
    role = _ref.role,
    restProps = _objectWithoutProperties(_ref, _excluded);
  var experiments = useExperimentContext();
  var theme = useThemeContext();
  var isUnderGroup = useGroupContext();
  // extracting aria props and rest props
  var _getAriaAttributes = getAriaAttributes(restProps),
    ariaAttributes = _getAriaAttributes.ariaAttributes,
    rest = _getAriaAttributes.rest;
  var ariaProps = _objectSpread(_objectSpread({}, ariaAttributes), {}, {
    role: role
  });
  var disabled = state === 'disabled';
  var _searchFieldStyleProp = searchFieldStylePropsMapper(disabled),
    backgroundColor = _searchFieldStyleProp.backgroundColor,
    placeholderTextColor = _searchFieldStyleProp.placeholderTextColor,
    inputTextColor = _searchFieldStyleProp.inputTextColor,
    labelTextColor = _searchFieldStyleProp.labelTextColor;
  var widthStyle = width === 'parent-fit' ? '100%' : width;
  var isPlaceholder = placeholderText !== '' && !inputText && !labelText;
  var interactiveState = interactiveStyle(experiments, theme, isPlaceholder);
  var radius = selectToken(theme.radius, 'base');
  var isZoomFontSizeFix = experiments.isZoomFontSizeFix,
    searchFieldMinHeight = experiments.searchFieldMinHeight;
  var _ref2 = isZoomFontSizeFix ? ['body-default', 'typography-type-1'] : ['body-s', 'typography-type-2'],
    _ref3 = _slicedToArray(_ref2, 2),
    typographyVariant = _ref3[0],
    typographyTestId = _ref3[1];
  var inputRef = useForwardedRef(ref);
  var realPlaceholderTextColor = selectToken(theme.text, placeholderTextColor);
  var realPlaceholderTextHoverFocusColor = selectToken(theme.text, 'product.primary');
  var inputVariant =
  /*#__PURE__*/
  // Need to set zIndex to 1 to prevent being covered by background
  React.createElement(Box, {
    width: "100%",
    zIndex: 1
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: typographyVariant,
    "data-testid": typographyTestId
  }, /*#__PURE__*/React.createElement(InputBaseMemoized, _extends({}, ariaProps, {
    ref: variant === 'input' ? inputRef : undefined,
    variant: "skeleton",
    value: inputText,
    label: ariaProps['aria-label'] || '',
    placeholder: placeholderText,
    placeholderTextColor: realPlaceholderTextColor,
    placeholderHoverFocusTextColor: realPlaceholderTextHoverFocusColor,
    onChange: onChange ? function (e) {
      onChange(e);
    } : undefined,
    leadingAsset: leadingAsset,
    trailingAsset: trailingAsset,
    state: disabled ? 'disabled' : undefined,
    clearLabel: clearLabel,
    onClear: onClear,
    required: required,
    selectOnFocus: selectOnFocus
  }))));
  var pickerVariant = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LeadingIconElement, {
    leadingAsset: leadingAsset,
    inputTextColor: inputTextColor
  }), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
    disabled: disabled
  }, !inputText && !labelText && /*#__PURE__*/React.createElement(Typography, {
    tag: "span",
    variant: typographyVariant,
    lineClamp: 1,
    textColor: placeholderTextColor
  }, placeholderText), inputText && /*#__PURE__*/React.createElement(Typography, {
    variant: typographyVariant,
    lineClamp: 1,
    textColor: inputTextColor
  }, inputText), labelText && /*#__PURE__*/React.createElement(Typography, {
    variant: "caption-default",
    lineClamp: 1,
    textColor: labelTextColor
  }, labelText)), /*#__PURE__*/React.createElement(TrailingIconElement, {
    trailingAsset: trailingAsset,
    inputTextColor: inputTextColor,
    disabled: disabled
  }));
  return /*#__PURE__*/React.createElement(SearchFieldContainer, _extends({}, rest, variant !== 'input' ? _objectSpread(_objectSpread({}, ariaProps), {}, {
    'aria-label': label
  }) : {}, {
    id: id,
    tabIndex: disabled || variant === 'input' ? -1 : 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: backgroundColor,
    cursor: disabled ? undefined : 'pointer',
    width: widthStyle
  }, searchFieldMinHeight ? {
    minHeight: '44px'
  } : {
    height: '44px'
  }, {
    disabled: disabled,
    px: "16",
    py: "4",
    onClick: onClick,
    onChange: variant === 'picker' ? onChange : undefined,
    onFocus: onFocus,
    onBlur: onBlur,
    ref: variant !== 'input' ? ref : undefined,
    $interactiveState: interactiveState,
    $radius: radius
  }, isUnderGroup && {
    className: 'child-group'
  }), variant === 'input' ? inputVariant : pickerVariant);
});
var SearchFieldMemoized = /*#__PURE__*/React.memo(SearchField);
SearchFieldMemoized.displayName = 'SearchField';

export { SearchFieldMemoized as SearchField };
//# sourceMappingURL=SearchField.js.map
